import { ServerInfo } from '../types/info';
import { safeIdCompare } from '../lib/utils';
import { SimpleUser, User } from '../types/user';
import { VenueRequest, VenueRequestStatus } from '../types/groupBooking';
import { ApiToken } from '../types/auth';
import { getMultiPropertyKind } from './libraries';
import { Reservation } from '../types/reservation';
import { ReserveTimelineItem } from '../types/reserve';
import { BareReserveSchedule, BareReserveScheduleRule } from '../types/reserveSchedule';

// main tabs
export function canViewPropertiesTab(user: User, serverInfo: ServerInfo) {
  if (user.superAdmin) return true;

  return serverInfo.instanceRole === 'brand';
}

export function canModifyBrandSettings(user: User, serverInfo: ServerInfo) {
  if (serverInfo.instanceRole !== 'brand') return false;

  if (user.superAdmin) return true;

  return user.brandAdmin;
}

export function canModifyBrandTranslation(user: User, serverInfo: ServerInfo) {
  if (serverInfo.instanceRole !== 'brand') return false;

  if (user.superAdmin) return true;

  return user.brandAdmin;
}

export function canEditItineraryInfo(user: User, serverInfo: ServerInfo) {
  if (!serverInfo.settings.ALLOW_ITINERARY_CHANGE) return false;

  if (user.superAdmin) return true;

  return user.shipAdmin;
}

export function canLockItineraryDay(user: User) {
  if (user.superAdmin) return true;

  return user.shipAdmin;
}

export function canViewUserManagementTab(user: User, serverInfo: ServerInfo) {
  if (!serverInfo.settings.ALLOW_USER_MANAGEMENT) return false;
  if (serverInfo.instanceRole === 'property') return false;

  if (user.superAdmin) return true;

  return user.userAdmin;
}

export function canViewPropertySettingsTab(user: User) {
  if (user.superAdmin) return true;

  return user.propertyAdmin;
}

export function canBookRequestsBypassingTimeRestrictions(user: User) {
  if (user.superAdmin) return true;

  return user.groupAdmin;
}

// dropdown items
export function canViewUserManagementDropdownItem(user: User, serverInfo: ServerInfo) {
  if (!serverInfo.settings.ALLOW_USER_MANAGEMENT) return false;
  if (serverInfo.instanceRole === 'brand') return false;

  if (user.superAdmin) return true;

  return user.userAdmin;
}

export function canViewGroupsMultiPropertyCheckbox(serverInfo: ServerInfo) {
  // disabled in property instances
  return serverInfo.instanceRole === 'brand';
}

// misc
export function canViewPropertyDetailsForm(user: User) {
  if (user.superAdmin) return true;

  return user.propertyAdmin;
}

export function canArchiveProperty(user: User, serverInfo: ServerInfo) {
  if (serverInfo.instanceRole !== 'brand') return false;

  if (user.superAdmin) return true;

  return user.brandAdmin;
}

export function canViewDigitalDailyProgramForm(user: User, serverInfo: ServerInfo) {
  if (user.superAdmin) return true;

  return user.shipAdmin && serverInfo.plugins.findIndex(p => p.name === 'ddp') > -1;
}

export function canViewNaveganteForm(user: User, serverInfo: ServerInfo) {
  if (user.superAdmin) return true;

  return user.shipAdmin && serverInfo.plugins.findIndex(p => p.name === 'navegante') > -1;
}

export function canEditFeed(user: User) {
  if (user.superAdmin) return true;

  return user.feedEditor;
}

export function canEditCanvas(user: User) {
  if (user.superAdmin) return true;

  return user.canvasEditor;
}

export function canEditSailingInfo(user: User) {
  if (user.superAdmin) return true;

  return user.shipAdmin;
}

export function canApproveOrRejectABooking(user: User, booking: VenueRequest) {
  if (user.superAdmin || user.groupAdmin) return true;
  return user.groupUser && safeIdCompare(user._id, booking.createdBy._id);
}

export function needsBookingChangeNotification(user: User, venueId: string) {
  return (user.groupApprovingVenues || []).findIndex(vId => safeIdCompare(vId, venueId)) > -1;
}

export function needsReservationChangeNotification(user: User, hostId?: string) {
  return user.reserveAdmin || (canHostEvents(user) && safeIdCompare(user._id, hostId));
}

export function canCreateOrEditBookings(user: User) {
  if (user.superAdmin) return true;

  return user.groupUser;
}

export function canManageAllReservations(user: User | ApiToken) {
  if (user.superAdmin) return true;

  return user.reservationManager;
}

export function canCreateSomeReservations(user: User) {
  if (user.superAdmin) return true;

  return user.reservationManager || canHostEvents(user);
}

export function canManageReservation(user: User, reservation: Reservation) {
  if (user.superAdmin) return true;

  return (
    user.reservationManager || (canHostEvents(user) && safeIdCompare(user._id, reservation.host))
  );
}

export function canManageTimelineItem(user: User, timelineItem: ReserveTimelineItem) {
  if (user.superAdmin) return true;

  return (
    user.reservationManager || (canHostEvents(user) && safeIdCompare(user._id, timelineItem.host))
  );
}

export function canCreateExperiences(user: User) {
  if (user.superAdmin) return true;

  return user.libraryAdmin;
}

export function canManageAllSchedules(user: User) {
  if (user.superAdmin) return true;

  return user.reserveAdmin;
}

export function canManageScheduleSomeRule(user: User, schedule?: BareReserveSchedule) {
  if (user.superAdmin) return true;

  return user.reserveAdmin || schedule?.rules.some(r => canManageScheduleRule(user, r));
}

export function canManageScheduleRule(user: User, rule: BareReserveScheduleRule) {
  if (user.superAdmin) return true;

  return user.reserveAdmin || (canHostEvents(user) && safeIdCompare(user._id, rule.host));
}

export function canManageReservePropertySettings(user: User) {
  if (user.superAdmin) return true;

  return user.reserveAdmin;
}

export function canViewReserveTab(user: User, serverInfo: ServerInfo) {
  if (!serverInfo.settings.ALLOW_RESERVE_MANAGEMENT) return false;

  if (user.superAdmin) return true;

  return user.reserveUser;
}

export function isPartialHost(user: User) {
  if (user.superAdmin) return true;

  return user.host;
}

export function canHostEvents(user: SimpleUser) {
  if (!user.host) return false;

  const { hasCompletedOnboarding, stripeAccountId } = user.reservablesConfig || {};
  return !!hasCompletedOnboarding && !!stripeAccountId;
}

export function canArchiveGroups(user: User) {
  if (user.superAdmin) return true;

  return user.groupAdmin;
}

export function canLinkTemplates(user: User) {
  if (user.superAdmin) return true;

  return user.shipAdmin;
}

export function canBulkLinkTemplates(user: User) {
  if (user.superAdmin) return true;

  return user.shipAdmin;
}

export function canChangeBookingStatus(
  status: VenueRequestStatus,
  user: User,
  booking: VenueRequest
) {
  if (user.superAdmin) return true;

  switch (status) {
    case 'approved':
    case 'rejected':
      return canApproveOrRejectABooking(user, booking);
    default:
      return canCreateOrEditBookings(user);
  }
}

export function canBulkSelectBookings(user: User) {
  if (user.superAdmin || user.groupAdmin) return true;

  return canCreateOrEditBookings(user);
}

export function canManageLibraries(user: User | ApiToken) {
  if (user.superAdmin) return true;

  return user.libraryAdmin;
}

export function canDeleteImages(user: User | ApiToken) {
  if (user.superAdmin) return true;

  return user.brandAdmin;
}

export function canManageItemPermissionLevel(user: User | ApiToken, serverInfo: ServerInfo) {
  if (serverInfo.instanceRole !== 'brand') return false;

  if (user.superAdmin) return true;

  return user.libraryAdmin && user.brandAdmin;
}

export function canEditImages(user: User | ApiToken) {
  if (user.superAdmin) return true;

  return user.libraryAdmin;
}

export function canModifyMultiPropertyItem<
  T extends { properties: Array<{ property: string }>; source?: string }
>(user: User | ApiToken, serverInfo: ServerInfo, propertyId: string, item?: T) {
  const canModifyLibraries = canManageLibraries(user);

  // Here I'm assuming we are editing always when we pass an item as props
  // Otherwise when we create items to library for example, it will fail since
  // We do not have the item yet.
  // Moreover, the checks below for property will not work since item is undefined
  // And the permissions checks for ship instance will always return false
  const isEditingItem = item !== undefined;
  if (!isEditingItem) {
    return canModifyLibraries;
  }

  // is brand, or source is property & is not multi
  const isBrandInstance = serverInfo.instanceRole === 'brand';
  const itemBelongsToProperty = safeIdCompare(item?.source, propertyId);
  const itemIsPropertyKind = getMultiPropertyKind(item).status === 'property';

  const isNotMulti = itemBelongsToProperty && itemIsPropertyKind;
  const check2 = isBrandInstance || isNotMulti;

  return canModifyLibraries && check2;
}

export function canViewJsonEditorButton(user: User) {
  return user.superAdmin;
}

export function canViewBlueprintEditorDropdownItem(user: User) {
  return user.superAdmin;
}

export function canSetSuperAdminConfig(user: User) {
  return user.superAdmin;
}

export function canPublishDay(user: User, serverInfo: ServerInfo) {
  if (serverInfo.settings.PUBLISHER_ROLE !== 'sender') return false;

  if (user.superAdmin) return true;

  return user.shipAdmin;
}

export function canCreateOrEditGroupResources(user: User, serverInfo: ServerInfo) {
  if (serverInfo.instanceRole !== 'brand') return false;

  if (user.superAdmin) return true;

  return user.groupAdmin;
}

export function canExportGroupLetters(user: User, serverInfo: ServerInfo) {
  const isPluginEnabled =
    serverInfo.plugins.findIndex(p => p.name === 'carnival-group-letters') > -1;
  if (user.superAdmin && isPluginEnabled) return true;

  return user.groupAdmin && isPluginEnabled;
}
